import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { supabase } from "@/integrations/supabase/client";

// Default Leaflet markers don't load via Vite — point them at CDN copies.
const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface Props {
  height?: number | string;
  className?: string;
}

const SRI_LANKA_CENTER: [number, number] = [7.8731, 80.7718];

export function ProjectMap({ height = 520, className }: Props) {
  const { data: projects } = useQuery({
    queryKey: ["map_projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, slug, title, hero_image, location, latitude, longitude, status")
        .not("latitude", "is", null)
        .not("longitude", "is", null);
      if (error) throw error;
      return data ?? [];
    },
    retry: 4,
    retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const pins = useMemo(
    () => (projects ?? []).filter((p: any) => p.latitude != null && p.longitude != null),
    [projects],
  );

  // Use imperative Leaflet (not react-leaflet) to avoid the
  // "Map container is already initialized" error caused by React StrictMode /
  // Suspense double-mounts. We fully tear down the map on unmount and clear
  // Leaflet's internal `_leaflet_id` from the DOM node so a remount works.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // If a previous instance left its id on the node, clear it first.
    if ((el as any)._leaflet_id != null) {
      delete (el as any)._leaflet_id;
    }

    const map = L.map(el, {
      center: SRI_LANKA_CENTER,
      zoom: 7,
      scrollWheelZoom: false,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const raf = requestAnimationFrame(() => map.invalidateSize());

    return () => {
      cancelAnimationFrame(raf);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      if (el && (el as any)._leaflet_id != null) {
        delete (el as any)._leaflet_id;
      }
    };
  }, []);

  // Sync markers when pins change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    pins.forEach((p: any) => {
      const marker = L.marker([p.latitude, p.longitude], { icon }).addTo(map);
      const safeTitle = String(p.title ?? "").replace(/</g, "&lt;");
      const safeLocation = p.location
        ? `<span class="text-xs text-muted-foreground">${String(p.location).replace(/</g, "&lt;")}</span>`
        : "";
      const safeImg = p.hero_image
        ? `<img src="${String(p.hero_image).replace(/"/g, "&quot;")}" alt="" class="mb-2 h-20 w-full rounded object-cover" />`
        : "";
      marker.bindPopup(
        `<a href="/projects/${encodeURIComponent(p.slug)}" class="block w-44">
          ${safeImg}
          <strong class="block text-sm leading-tight">${safeTitle}</strong>
          ${safeLocation}
        </a>`,
      );
      markersRef.current.push(marker);
    });
  }, [pins]);

  return (
    <div className={className} style={{ height }}>
      <div
        ref={containerRef}
        className="h-full w-full overflow-hidden rounded-2xl"
      />
      {pins.length === 0 && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Add coordinates to projects in the admin to see them pinned on the map.
        </p>
      )}
    </div>
  );
}
